<script setup>
import { computed } from "vue"
import { t } from '@/i18n'
import { mediaTypeRef } from '@/model/media'
import { Carousel, Navigation, Slide } from 'vue3-carousel'
import { appNameRef, configMOdel } from '@/model/basic'

const { toCustomerURLFunc } = configMOdel(true)

const partnerItems = [
    { id: 'house-sigma', name: 'HouseSigma', brand: 'house-sigma' },
    { id: 'swica', name: 'SWICA', brand: 'swica' },
    { id: 'local-logic', name: 'Local Logic', brand: 'local-logic' },
    { id: 'century-21', name: 'Century 21', brand: 'century-21' },
    { id: 'microsoft', name: 'Microsoft', brand: 'microsoft' },
]

const partnerMetrics = [
    {
        icon: 'network',
        value: '50+',
        label: t('Partner Networks'),
        description: t('Trusted partnerships across Canada'),
    },
    {
        icon: 'market',
        value: '12+',
        label: t('Markets Covered'),
        description: t('Major real estate markets across Canada'),
    },
    {
        icon: 'property',
        value: '1000+',
        label: t('Properties Promoted'),
        description: t('Helping agents and brands grow their business in Canada'),
    },
]

const logoItemsToShow = computed(()=> {
    let _val = mediaTypeRef.value
    let _res = 1.2
    if(_val == '2xl' || _val == 'xl') {
        _res = 5
    }else if(_val == 'lg') {
        _res = 4
    }else if(_val == 'md') {
        _res = 3
    }else if(_val == 'sm') {
        _res = 2
    }
  return _res
})

const showPartnerNavigation = computed(() => logoItemsToShow.value < partnerItems.length)
</script>

<template>
    <footer class="w-full bg-gray-900 text-white block overflow-hidden">
        <section class="partners-showcase" aria-labelledby="partners-title">
            <span class="partners-city" aria-hidden="true"></span>
            <span class="partners-dots" aria-hidden="true"></span>
            <div class="partners-shell">
                <div class="partners-top">
                    <div class="partners-intro">
                        <p class="partners-eyebrow">{{t('OUR PARTNERS')}}</p>
                        <span class="partners-accent" aria-hidden="true"></span>
                        <h2 id="partners-title">{{t('Building Stronger')}}<br>{{t('Connections')}}</h2>
                        <p class="partners-subtitle">{{t('Across the Real Estate Industry')}}<br>{{t('in')}} <strong>{{t('Canada')}}</strong></p>
                        <p class="partners-description">{{t('We collaborate with leading real estate developers, brokerages, and service providers to bring more opportunities to agents, businesses, and clients.')}}</p>
                    </div>

                    <div class="partners-metrics" :aria-label="t('Partner network highlights')">
                        <article v-for="metric in partnerMetrics" :key="metric.label" class="partner-metric">
                            <span class="metric-icon" aria-hidden="true">
                                <svg v-if="metric.icon === 'network'" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="8" r="3"/><circle cx="5.5" cy="10" r="2.3"/><circle cx="18.5" cy="10" r="2.3"/><path d="M7.5 20v-1.4a4.5 4.5 0 0 1 4.5-4.5 4.5 4.5 0 0 1 4.5 4.5V20M2 19v-1a3.4 3.4 0 0 1 4.8-3.1M22 19v-1a3.4 3.4 0 0 0-4.8-3.1"/>
                                </svg>
                                <svg v-else-if="metric.icon === 'market'" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="9"/><path d="M3.5 9h17M3.5 15h17M12 3c2.2 2.4 3.2 5.4 3.2 9S14.2 18.6 12 21M12 3C9.8 5.4 8.8 8.4 8.8 12S9.8 18.6 12 21"/>
                                </svg>
                                <svg v-else viewBox="0 0 24 24" fill="none">
                                    <path d="M3 11.5 12 4l9 7.5M5.5 10.5V21h13V10.5M9 21v-6h6v6M8 8V5h3"/>
                                </svg>
                            </span>
                            <strong class="metric-value">{{metric.value}}</strong>
                            <h3>{{metric.label}}</h3>
                            <span class="metric-divider" aria-hidden="true"></span>
                            <p>{{metric.description}}</p>
                        </article>
                    </div>
                </div>

                <div class="partner-carousel">
                    <Carousel
                        id="footer-logo"
                        :items-to-show="logoItemsToShow"
                        :wrap-around="false"
                        :mouse-drag="false"
                        :touch-drag="false"
                        :transition="350"
                        snapAlign="start"
                    >
                        <Slide v-for="item in partnerItems" :key="item.id">
                            <article class="partner-card">
                                <div class="partner-logo" :class="`partner-logo-${item.brand}`" :aria-label="`${item.name} logo`" role="img">
                                    <svg v-if="item.brand === 'house-sigma'" viewBox="0 0 180 100" fill="none" aria-hidden="true">
                                        <path d="M56 42 90 14l34 28v40H56V42Z"/>
                                        <circle cx="90" cy="57" r="20"/><circle cx="90" cy="57" r="9"/>
                                        <path d="m106 73 20 17M74 91h32"/>
                                    </svg>
                                    <div v-else-if="item.brand === 'swica'" class="swica-mark" aria-hidden="true">
                                        <svg viewBox="0 0 110 90" fill="none">
                                            <path d="M55 53S23 36 23 20c0-13 17-18 25-6l7 10 7-10c8-12 25-7 25 6 0 16-32 33-32 33Z"/>
                                            <path d="M8 69h28l12-10 12 5h22c8 0 14 4 18 10L57 84 34 75H8"/>
                                            <path d="m42 29 8-8 8 14 8-11 8 6"/>
                                        </svg>
                                        <strong>SWICA</strong>
                                    </div>
                                    <div v-else-if="item.brand === 'local-logic'" class="local-logic-mark" aria-hidden="true">
                                        <svg viewBox="0 0 70 70" fill="none">
                                            <path d="M8 42V12h30M62 28v30H32"/>
                                            <path d="M18 52V22h30M52 18v30H22"/>
                                        </svg>
                                        <strong>Local Logic</strong>
                                    </div>
                                    <div v-else-if="item.brand === 'century-21'" class="century-mark" aria-hidden="true">
                                        <div><strong>Century</strong><b>21</b></div>
                                        <span></span>
                                    </div>
                                    <div v-else class="microsoft-mark" aria-hidden="true">
                                        <span class="microsoft-squares"><i></i><i></i><i></i><i></i></span>
                                        <strong>Microsoft</strong>
                                    </div>
                                </div>
                                <span class="partner-card-line" aria-hidden="true"></span>
                                <p>{{item.name}}</p>
                            </article>
                        </Slide>

                        <template #addons>
                            <Navigation v-if="showPartnerNavigation" />
                        </template>
                    </Carousel>
                </div>
            </div>
        </section>

        <section class="w-full py-[70px] border-t border-white/20 relative">
            <em class="w-full h-full bg-fill absolute left-0 top-0 opacity-[0.04]" :style="'background-image: url('+require('@/assets/img/bg-footer.jpg')+')'"></em>
            <div class="ctx-wrap relative">
                <div class="w-full md:w-1/2 lg:w-1/3">
                    <h5 class="pb-6 mb-8 text-sm text-third font-bold border-b border-white/20">{{t('关于我们')}}</h5>
                    <div class="text-sm leading-7">
                        <p>{{t('底部-about')}}</p>
                    </div>
                </div>
                <div class="w-full md:w-1/2 lg:w-2/3 mt-14">
                    <div class="pt-6 pl-11 pr-8 pb-4 bg-black bg-opacity-[0.05] border border-third relative">
                        <div class="w-full relative flex">
                            <div class="flex-1 overflow-hidden">
                                <p class="text-xs text-third font-medium">{{t('实时聊天支持')}} :</p>
                            </div>
                            <button @click="toCustomerURLFunc()" class="px-5 h-12 text-xs ctx-third hover:bg-minor font-medium rounded cursor-pointer flex items-center">
                                <span>{{t('在线聊天')}}</span>
                                <svg class="w-4 h-4 ml-1" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                    <path d="M4.02381 3.78307C4.12549 3.32553 4.5313 3 5 3H19C19.4687 3 19.8745 3.32553 19.9762 3.78307L21.9762 12.7831C21.992 12.8543 22 12.927 22 13V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V13C2 12.927 2.00799 12.8543 2.02381 12.7831L4.02381 3.78307ZM5.80217 5L4.24662 12H9C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12H19.7534L18.1978 5H5.80217ZM16.584 14C15.8124 15.7659 14.0503 17 12 17C9.94968 17 8.1876 15.7659 7.41604 14H4V19H20V14H16.584Z"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <section class="w-full pt-8 pb-5 bg-[#061B41] text-white font-medium">
            <div class="ctx-wrap flex flex-row items-center justify-between gap-1 sm:justify-start sm:gap-8">
                <p class="text-[10px] sm:text-xs whitespace-nowrap">© {{appNameRef}} 2026 . {{t('All rights reserved.')}}</p>
                <div class="text-[10px] sm:text-xs text-white flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                    <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
                        <path d="m4.5 7 6.35 5.08a1.83 1.83 0 0 0 2.3 0L19.5 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span>support@officialcentris.com</span>
                </div>
            </div>
        </section>
    </footer>
</template>

<style scoped>
.partners-showcase {
    --partners-navy: #031a36;
    --partners-card: rgba(3, 32, 61, .88);
    --partners-line: rgba(74, 147, 196, .42);
    --partners-orange: #f79c41;
    position: relative;
    isolation: isolate;
    overflow: hidden;
    padding: 82px 0 66px;
    color: #fff;
    background:
        radial-gradient(circle at 32% 36%, rgba(0, 117, 186, .12), transparent 28%),
        linear-gradient(115deg, #02162f 0%, var(--partners-navy) 54%, #06284a 100%);
}

.partners-city {
    position: absolute;
    z-index: -2;
    right: -2%;
    bottom: 15%;
    width: 58%;
    height: 46%;
    opacity: .16;
    background: url('@/assets/img/bg-city.png') right bottom / contain no-repeat;
    filter: sepia(1) saturate(3) hue-rotate(345deg) brightness(1.25);
}

.partners-dots {
    position: absolute;
    z-index: -1;
    top: 7%;
    left: 23%;
    width: 29rem;
    height: 22rem;
    opacity: .25;
    transform: rotate(-7deg);
    background-image: radial-gradient(circle, #1d9dd8 1.6px, transparent 1.8px);
    background-size: 9px 9px;
    mask-image: radial-gradient(ellipse at center, #000 0 42%, transparent 72%);
    pointer-events: none;
}

.partners-shell {
    position: relative;
    z-index: 1;
    width: min(1226px, calc(100% - 32px));
    margin-inline: auto;
}

.partners-top {
    display: grid;
    grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr);
    align-items: center;
    gap: 58px;
}

.partners-eyebrow {
    margin: 0;
    color: var(--partners-orange);
    font-family: 'Montserrat', sans-serif;
    font-size: .82rem;
    font-weight: 800;
    letter-spacing: .08em;
}

.partners-accent {
    display: block;
    width: 46px;
    height: 3px;
    margin: 17px 0 24px;
    border-radius: 999px;
    background: var(--partners-orange);
}

.partners-intro h2 {
    margin: 0;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: clamp(2.7rem, 4.2vw, 4.35rem);
    line-height: 1.02;
    font-weight: 700;
    letter-spacing: -.035em;
}

.partners-subtitle {
    margin: 18px 0 0;
    color: #a9c5e5;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: clamp(1.45rem, 2.1vw, 2.15rem);
    line-height: 1.32;
}

.partners-subtitle strong {
    color: var(--partners-orange);
    font-weight: 700;
}

.partners-description {
    max-width: 530px;
    margin: 24px 0 0;
    color: rgba(235, 244, 255, .82);
    font-size: .96rem;
    line-height: 1.8;
}

.partners-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.partner-metric {
    min-width: 0;
    padding: 20px 24px 16px;
    text-align: center;
    border-left: 1px solid rgba(106, 165, 205, .36);
}

.partner-metric:first-child {
    border-left: 0;
}

.metric-icon {
    display: grid;
    width: 52px;
    height: 52px;
    margin: 0 auto 18px;
    place-items: center;
    color: var(--partners-orange);
}

.metric-icon svg {
    width: 100%;
    height: 100%;
    stroke: currentColor;
    stroke-width: 1.45;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.metric-value {
    display: block;
    font-size: clamp(2.75rem, 4vw, 4.25rem);
    line-height: 1;
    font-weight: 500;
    letter-spacing: -.045em;
}

.partner-metric h3 {
    min-height: 42px;
    margin: 14px 0 0;
    font-size: .96rem;
    line-height: 1.35;
    font-weight: 700;
}

.metric-divider {
    display: block;
    width: 100%;
    height: 1px;
    margin: 20px auto 16px;
    background: linear-gradient(90deg, transparent, rgba(91, 156, 201, .34), transparent);
}

.partner-metric p {
    margin: 0 auto;
    color: rgba(222, 236, 250, .72);
    font-size: .78rem;
    line-height: 1.65;
}

.partner-carousel {
    margin-top: 52px;
}

.partner-carousel :deep(.carousel__viewport) {
    overflow: hidden;
}

.partner-carousel :deep(.carousel__slide) {
    padding: 0 7px;
}

.partner-carousel :deep(.carousel__prev),
.partner-carousel :deep(.carousel__next) {
    width: 42px;
    height: 42px;
    margin: 0;
    color: #fff;
    border: 1px solid rgba(77, 159, 216, .68);
    border-radius: 50%;
    background: rgba(2, 25, 52, .94);
    box-shadow: 0 8px 22px rgba(0, 0, 0, .28);
}

.partner-carousel :deep(.carousel__prev) {
    left: 5px;
}

.partner-carousel :deep(.carousel__next) {
    right: 5px;
}

.partner-carousel :deep(.carousel__prev:hover),
.partner-carousel :deep(.carousel__next:hover) {
    color: #fff;
    border-color: #17b6df;
    background: #075083;
}

.partner-carousel :deep(.carousel__prev--disabled),
.partner-carousel :deep(.carousel__next--disabled) {
    opacity: .32;
}

.partner-card {
    width: 100%;
    min-height: 188px;
    padding: 24px 18px 20px;
    border: 1px solid var(--partners-line);
    border-radius: 4px;
    background: linear-gradient(145deg, rgba(5, 39, 70, .95), var(--partners-card));
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .015);
}

.partner-logo {
    display: flex;
    width: 100%;
    height: 95px;
    align-items: center;
    justify-content: center;
}

.partner-logo-house-sigma svg {
    width: 106px;
    height: 88px;
    color: #08b9c1;
    stroke: currentColor;
    stroke-width: 5;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.swica-mark,
.local-logic-mark,
.microsoft-mark {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
}

.swica-mark svg {
    width: 70px;
    height: 66px;
    stroke: #09b9bd;
    stroke-width: 4;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.swica-mark strong {
    color: #fff;
    font-family: Arial, sans-serif;
    font-size: 1.72rem;
    font-weight: 600;
    letter-spacing: .015em;
}

.local-logic-mark svg {
    width: 48px;
    height: 48px;
    stroke: #62d600;
    stroke-width: 7;
    stroke-linecap: square;
}

.local-logic-mark strong {
    color: #fff;
    font-family: Arial, sans-serif;
    font-size: 1.42rem;
    font-weight: 500;
    white-space: nowrap;
}

.century-mark {
    color: #fff;
    font-family: Arial, sans-serif;
    text-align: center;
}

.century-mark > div {
    display: flex;
    align-items: baseline;
    justify-content: center;
    letter-spacing: -.07em;
}

.century-mark strong {
    font-size: 2rem;
    font-weight: 700;
}

.century-mark b {
    margin-left: 3px;
    font-size: 2.55rem;
    font-weight: 800;
}

.century-mark > span {
    display: block;
    width: 112px;
    height: 10px;
    margin: -5px auto 0;
    border-top: 3px solid #f4c400;
    border-left: 3px solid #f4c400;
    transform: skewX(-30deg);
}

.microsoft-squares {
    display: grid;
    width: 45px;
    height: 45px;
    flex: 0 0 auto;
    grid-template-columns: repeat(2, 1fr);
    gap: 3px;
}

.microsoft-squares i:nth-child(1) { background: #f25022; }
.microsoft-squares i:nth-child(2) { background: #7fba00; }
.microsoft-squares i:nth-child(3) { background: #00a4ef; }
.microsoft-squares i:nth-child(4) { background: #ffb900; }

.microsoft-mark strong {
    color: #fff;
    font-family: Arial, sans-serif;
    font-size: 1.5rem;
    font-weight: 500;
}

.partner-card-line {
    display: block;
    width: 36px;
    height: 2px;
    margin: 7px auto 13px;
    background: linear-gradient(90deg, #02b9c2, #1e91e6);
}

.partner-card p {
    margin: 0;
    color: rgba(255, 255, 255, .9);
    font-size: .95rem;
    font-weight: 700;
    text-align: center;
}

@media (max-width: 1023px) {
    .partners-showcase {
        padding: 70px 0 58px;
    }

    .partners-top {
        grid-template-columns: 1fr;
        gap: 42px;
    }

    .partners-intro {
        max-width: 660px;
    }

    .partners-city {
        width: 90%;
        height: 34%;
        opacity: .12;
    }

    .partners-dots {
        left: auto;
        right: -8rem;
    }
}

@media (max-width: 639px) {
    .partners-showcase {
        padding: 54px 0 46px;
    }

    .partners-shell {
        width: min(100% - 28px, 1226px);
    }

    .partners-top {
        gap: 34px;
    }

    .partners-intro h2 {
        font-size: clamp(2.35rem, 12vw, 3.1rem);
    }

    .partners-description {
        font-size: .9rem;
    }

    .partners-metrics {
        grid-template-columns: 1fr;
        border: 1px solid rgba(106, 165, 205, .3);
    }

    .partner-metric {
        display: grid;
        grid-template-columns: 56px 1fr;
        grid-template-areas:
            'icon value'
            'icon label'
            'description description';
        align-items: center;
        padding: 22px;
        text-align: left;
        border-left: 0;
        border-top: 1px solid rgba(106, 165, 205, .3);
    }

    .partner-metric:first-child {
        border-top: 0;
    }

    .metric-icon {
        grid-area: icon;
        width: 42px;
        height: 42px;
        margin: 0;
    }

    .metric-value {
        grid-area: value;
        font-size: 2.7rem;
    }

    .partner-metric h3 {
        grid-area: label;
        min-height: 0;
        margin: 4px 0 0;
    }

    .metric-divider {
        display: none;
    }

    .partner-metric p {
        grid-area: description;
        width: 100%;
        margin: 16px 0 0;
        padding-top: 14px;
        text-align: left;
        border-top: 1px solid rgba(91, 156, 201, .24);
    }

    .partner-carousel {
        margin-top: 34px;
    }

}

@media (prefers-reduced-motion: reduce) {
    .partner-carousel :deep(.carousel__track) {
        transition: none !important;
    }
}
</style>
